/**
 * Scope-of-work checklist — "what each job should have," ported from the PDQ
 * Scope Tracker's SCOPE_TEMPLATE. Grouped by job type (Water Mitigation / Fire &
 * Smoke / Reconstruction) → section → items. Each item gets a stable id so its
 * per-job status (pending / done / not_needed) can be saved against it.
 *
 * Shared so the phone (field execution) and the web (office oversight) show the
 * same steps.
 */

export type ScopeStatus = 'pending' | 'done' | 'not_needed';

export interface ScopeItem { id: string; label: string }
export interface ScopeSection { section: string; items: ScopeItem[] }
export interface ScopeGroup { group: string; sections: ScopeSection[] }

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function build(group: string, sections: [string, string[]][]): ScopeGroup {
  return {
    group,
    sections: sections.map(([section, labels]) => ({
      section,
      items: labels.map((label) => ({ id: slug(`${group}-${section}-${label}`), label })),
    })),
  };
}

export const SCOPE_TEMPLATE: ScopeGroup[] = [
  build('Water Mitigation', [
    ['Extraction', ['Weighted wand extraction', 'Wand extraction']],
    ['Antimicrobial', ['Antimicrobial application']],
    ['Contents (Emergency)', ['Contents manipulation']],
    ['Containment Chamber', ['Poly barrier install', 'Negative air machine', 'Decon chamber', 'Warning signage', 'Zip wall']],
    ['Equipment / Stabilization', ['Dehumidifier placement', 'Portable drainage setup', 'Air scrubber placement', 'Hydroxyl generator']],
    ['Demolition', ['Drill holes for drying', 'Subfloor inspection', 'Flood cut drywall', 'Remove insulation', 'Remove ceiling drywall', 'Remove ceiling insulation']],
    ['Flooring', ['Carpet', 'Padding', 'Tack strip', 'Vinyl', 'Laminate', 'Hardwood', 'Tile', 'Scrape']],
    ['Cleaning', ['HEPA vacuum', 'Antimicrobial wipe-down', 'Fogging', 'Air duct cleaning', 'Duct sealing']],
    ['Drying', ['Air mover placement', 'Dehumidifier check', 'Moisture readings']],
    ['Asbestos / Lead', ['Asbestos sample collected', 'Lead test completed']],
    ['Trash / Debris', ['Pickup truck haul-out', 'Dumpster fill', 'Bag debris on-site']],
    ['General / Daily', ['Set up PPE', 'Check & log all equipment', 'Moisture readings — AM', 'Moisture readings — PM', 'Psychrometric readings', 'Photo documentation', 'Safety walk', 'Client communication log', 'End-of-day equipment check']],
    ['Electrician', ['Ceiling fan disconnect', 'Dishwasher circuit', 'Microwave circuit', 'Garbage disposal', 'Electric safety check', 'Other — electrical']],
    ['Plumber', ['Remove dishwasher', 'Remove faucets/drain', 'Remove toilet', 'Other — plumbing']],
  ]),
  build('Fire & Smoke', [
    ['Fire Containment', ['Board up / secure structure', 'Set up containment for smoke areas']],
    ['Demolition', ['Remove debris / charred materials', 'Remove smoke-damaged insulation']],
    ['Smoke Cleaning', ['HEPA vacuum soot from surfaces', 'Dry sponge walls & ceilings', 'Clean / deodorize affected contents', 'Ozone / hydroxyl treatment', 'Thermal fogging', 'Seal / encapsulate smoke-damaged surfaces']],
    ['Documentation', ['Photo-document all affected areas']],
  ]),
  build('Reconstruction', [
    ['Drywall', ['Hang new drywall', 'Tape & mud drywall', 'Sand & prep drywall for paint']],
    ['Finishing', ['Prime walls / ceilings', 'Paint walls / ceilings', 'Install new baseboards', 'Install trim / door casing', 'Install new cabinetry', 'Touch-up paint']],
    ['Flooring', ['Install new flooring', 'Install new carpet & pad']],
    ['Rough-In', ['Install new insulation', 'Plumbing rough-in / repair', 'Electrical rough-in / repair']],
    ['Closeout', ['Final clean / detail', 'Final walk-through / punch list']],
  ]),
];

/** Division code → the scope group most relevant to it (for default filtering). */
export const DIVISION_SCOPE_GROUP: Record<string, string> = {
  WTR: 'Water Mitigation', MLD: 'Water Mitigation', STC: 'Water Mitigation',
  ENV: 'Water Mitigation', ASB: 'Water Mitigation',
  REC: 'Reconstruction', CON: 'Reconstruction', CPO: 'Reconstruction',
  TRM: 'Fire & Smoke',
};

export const SCOPE_ITEM_COUNT = SCOPE_TEMPLATE.reduce(
  (n, g) => n + g.sections.reduce((m, s) => m + s.items.length, 0), 0,
);

/** Cycle a status pending → done → not_needed → pending. */
export function nextScopeStatus(s: ScopeStatus): ScopeStatus {
  return s === 'pending' ? 'done' : s === 'done' ? 'not_needed' : 'pending';
}
